import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getPointsBalance, transferPoints, getPointsTransactions } from '../../store/slices/pointsSlice';
import { getAuraScore } from '../../store/slices/auraSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import styled from 'styled-components';
import { formatAuraScoreDisplay } from '../../utils/formatAuraScore';
import { formatWalletBalanceEgp } from '../../utils/formatPrice';
import WalletIcon from '../../components/icons/WalletIcon';
import { REFERRAL_REWARD_EGP } from '../../constants/referralProgram';

const PointsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
  min-height: 100vh;
  background: linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%);
`;

const PageTitle = styled.h1`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.65rem;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2rem;
  text-align: center;
`;

const TitleSegment = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 0 0.35rem;
`;

const TitleIconBack = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  opacity: 0.2;
  pointer-events: none;
  user-select: none;
  line-height: 1;
`;

const TitleText = styled.span`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const TitleInlineIcon = styled.span`
  font-size: 1.35rem;
  line-height: 1;
`;

const PointsLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 1.5rem 2rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const WalletColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const PointsCard = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.75rem;
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
`;

const TransferCard = styled(PointsCard)`
  padding: 1.35rem 1.5rem;
`;

const CardTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.85rem 0;
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const TransferForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.35rem;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0.375rem;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgb(16 185 129 / 0.1);
  }
`;

const Button = styled.button`
  background-color: var(--primary-color);
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--primary-dark);
  }
  
  &:disabled {
    background-color: var(--text-light);
    cursor: not-allowed;
  }
`;

const TransactionList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
`;

const TransactionInfo = styled.div`
  flex: 1;
`;

const TransactionType = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const TransactionDate = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const TransactionAmount = styled.div`
  font-weight: 600;
  color: ${props => props.positive ? 'var(--success-color)' : 'var(--error-color)'};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;

const OverviewCard = styled(PointsCard)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
`;

const WalletBadgeCard = styled.div`
  background: linear-gradient(135deg, #0f766e 0%, #14b8a6 55%, #5eead4 100%);
  color: #fff;
  border-radius: 0.75rem;
  padding: 1.15rem 1.35rem 1.25rem;
  box-shadow: var(--shadow-sm);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  width: 100%;
`;

const WalletBadgeHeading = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
`;

const WalletBadgeValue = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 1.85rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
`;

const WalletBadgeDetails = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  opacity: 0.95;

  li + li {
    margin-top: 0.35rem;
  }
`;

const HistoryCard = styled(PointsCard)`
  margin-top: 1.5rem;
`;

const WalletInlineLink = styled(Link)`
  color: #fff;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    opacity: 0.9;
  }
`;

const SectionHeading = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0;
`;

const OverviewValue = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 2.25rem;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1.1;
`;

const AuraValueIcon = styled.span`
  font-size: 1.5rem;
  line-height: 1;
`;

const DetailBlock = styled.div`
  margin-top: 0.25rem;
`;

const DetailTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
`;

const DetailList = styled.ul`
  margin: 0;
  padding-left: 1.15rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.55;

  li + li {
    margin-top: 0.45rem;
  }
`;

const AuraBenefitsIntro = styled.p`
  color: var(--text-secondary);
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  line-height: 1.55;
`;

const AuraBenefitsFootnote = styled.p`
  color: var(--text-secondary);
  font-size: 0.8rem;
  margin: 0;
  line-height: 1.5;
`;

const PointsPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { balance, transactions = [], loading, error } = useSelector((state) => state.points);
  const { user } = useSelector((state) => state.auth);
  
  const [transferForm, setTransferForm] = useState({
    username: '',
    phone_number: '',
    amount: ''
  });

  useEffect(() => {
    if (user?.id) {
      dispatch(getPointsBalance());
      dispatch(getPointsTransactions());
      dispatch(getAuraScore());
    }
  }, [dispatch, user]);

  const { score: auraScore, loading: auraLoading } = useSelector((state) => state.aura);

  const walletBullet3Parts = useMemo(() => {
    const giftsLabel = t('points.giftsLink');
    const bullet = t('points.walletBullet3', { amount: REFERRAL_REWARD_EGP });
    const idx = bullet.indexOf(giftsLabel);
    if (idx === -1) {
      return { before: bullet, after: '' };
    }
    return {
      before: bullet.slice(0, idx),
      after: bullet.slice(idx + giftsLabel.length),
    };
  }, [t]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleTransferChange = (e) => {
    const { name, value } = e.target;
    setTransferForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    if (!transferForm.username || !transferForm.phone_number || !transferForm.amount) {
      toast.error(t('points.missingFieldsBody'));
      return;
    }

    // Prevent self-transfer
    if (transferForm.username.toLowerCase() === (user?.username || '').toLowerCase()) {
      toast.error(t('points.cannotTransferSelf'));
      return;
    }

    const amount = parseFloat(transferForm.amount);
    if (amount <= 0) {
      toast.error(t('points.invalidAmountBody'));
      return;
    }

    if (amount > balance) {
      toast.error(t('points.insufficientPoints'));
      return;
    }

    dispatch(transferPoints({
      username: transferForm.username.trim().replace(/^@/, ''),
      phone_number: transferForm.phone_number.trim(),
      amount: transferForm.amount
    }));
    setTransferForm({ username: '', phone_number: '', amount: '' });
  };

  if (loading) {
    return <LoadingSpinner text={t('points.loading')} />;
  }

  return (
    <PointsContainer>
      <PageTitle>
        <TitleSegment>
          <TitleIconBack aria-hidden>⭐</TitleIconBack>
          <TitleText>
            <TitleInlineIcon aria-hidden>⭐</TitleInlineIcon>
            {t('points.pageTitleAura')}
          </TitleText>
        </TitleSegment>
        <TitleText>{t('points.titleAnd')}</TitleText>
        <TitleSegment>
          <TitleText>
            <WalletIcon size={22} color="var(--text-primary)" />
            {t('points.pageTitleWallet')}
          </TitleText>
        </TitleSegment>
      </PageTitle>

      <PointsLayout>
        <WalletColumn>
          <WalletBadgeCard>
            <WalletBadgeHeading>
              <WalletIcon size={18} color="#fff" />
              {t('points.walletBalance')}
            </WalletBadgeHeading>
            <WalletBadgeValue>
              <WalletIcon size={24} color="#fff" />
              {formatWalletBalanceEgp(balance || 0)}
            </WalletBadgeValue>
            <WalletBadgeDetails>
              <li>{t('points.walletBullet1')}</li>
              <li>{t('points.walletBullet2')}</li>
              <li>
                {walletBullet3Parts.before}
                <WalletInlineLink to="/giftcards">{t('points.giftsLink')}</WalletInlineLink>
                {walletBullet3Parts.after}
              </li>
            </WalletBadgeDetails>
          </WalletBadgeCard>

          <TransferCard>
            <CardTitle>💸 {t('points.transferTitle')}</CardTitle>
            <TransferForm onSubmit={handleTransfer}>
              <FormGroup>
                <Label htmlFor="username">{t('points.recipientUsername')}</Label>
                <Input
                  type="text"
                  id="username"
                  name="username"
                  value={transferForm.username}
                  onChange={handleTransferChange}
                  placeholder={t('points.placeholderUsername')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone_number">{t('points.recipientPhone')}</Label>
                <Input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={transferForm.phone_number}
                  onChange={handleTransferChange}
                  placeholder={t('points.placeholderPhone')}
                  autoComplete="tel"
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="amount">{t('points.amount')}</Label>
                <Input
                  type="number"
                  id="amount"
                  name="amount"
                  value={transferForm.amount}
                  onChange={handleTransferChange}
                  placeholder={t('points.placeholderAmount')}
                  min="1"
                  max={balance}
                />
              </FormGroup>

              <Button type="submit" disabled={!transferForm.username || !transferForm.phone_number || !transferForm.amount}>
                {t('points.transferSubmit')}
              </Button>
            </TransferForm>
          </TransferCard>
        </WalletColumn>

        <OverviewCard>
          <SectionHeading>
            <span aria-hidden>⭐</span>
            {t('points.auraScore')}
          </SectionHeading>
          <OverviewValue>
            {auraLoading ? (
              '…'
            ) : (
              <>
                <AuraValueIcon aria-hidden>⭐</AuraValueIcon>
                {formatAuraScoreDisplay(auraScore)}
              </>
            )}
          </OverviewValue>
          <DetailBlock>
            <DetailTitle>⭐ {t('points.auraBenefitsTitle')}</DetailTitle>
            <AuraBenefitsIntro>
              {t('points.auraBenefitsIntro')}
            </AuraBenefitsIntro>
            <DetailTitle>{t('points.higherAuraTitle')}</DetailTitle>
            <DetailList>
              <li>{t('points.higherAura1')}</li>
              <li>{t('points.higherAura2')}</li>
              <li>{t('points.higherAura3')}</li>
            </DetailList>
            <DetailTitle>{t('points.auraRewardsTitle')}</DetailTitle>
            <DetailList>
              <li>{t('points.auraReward1')}</li>
              <li>{t('points.auraReward2')}</li>
              <li>{t('points.auraReward3')}</li>
              <li>{t('points.auraReward4')}</li>
            </DetailList>
            <AuraBenefitsFootnote>
              {t('points.auraFootnote')}
            </AuraBenefitsFootnote>
          </DetailBlock>
          <DetailBlock>
            <DetailTitle>{t('points.gainAura')}</DetailTitle>
            <DetailList>
              <li>{t('points.gainAura1')}</li>
              <li>{t('points.gainAura2')}</li>
            </DetailList>
          </DetailBlock>
          <DetailBlock>
            <DetailTitle>{t('points.loseAura')}</DetailTitle>
            <DetailList>
              <li>{t('points.loseAura1')}</li>
              <li>{t('points.loseAura2')}</li>
              <li>{t('points.loseAura3')}</li>
            </DetailList>
          </DetailBlock>
        </OverviewCard>
      </PointsLayout>

      <HistoryCard>
        <CardTitle>📊 {t('points.transactionHistory')}</CardTitle>
        <TransactionList>
          {transactions && transactions.length > 0 ? (
            transactions
              .filter(transaction => transaction && transaction.transaction_type) // Filter out undefined/null transactions
              .map((transaction, index) => {
                // Determine if this transaction is positive or negative for the current user
                let isPositive = false;
                let displayAmount = parseFloat(transaction.amount);
                let description = transaction.transaction_type;

                if (transaction.transaction_type === 'TRANSFER') {
                  if (transaction.from_user_id === user?.id) {
                    // User sent points - negative for them
                    isPositive = false;
                    displayAmount = -displayAmount;
                    // Show phone number if available, otherwise show user ID
                    const recipientPhone = transaction.to_phone_number || t('points.txUserFallback');
                    description = t('points.txSent', { phone: recipientPhone });
                  } else if (transaction.to_user_id === user?.id) {
                    // User received points - positive for them
                    isPositive = true;
                    // Show phone number if available, otherwise show generic label
                    const senderPhone = transaction.from_phone_number || t('points.txUserFallback');
                    description = t('points.txReceived', { phone: senderPhone });
                  }
                } else if (transaction.transaction_type === 'INIT') {
                  // Initial points are always positive
                  isPositive = true;
                  description = t('points.txInitial');
                } else if (transaction.transaction_type === 'SALE') {
                  isPositive = transaction.to_user_id === user?.id;
                  description = t('points.txSale');
                } else if (transaction.transaction_type === 'COMMISSION') {
                  isPositive = false;
                  displayAmount = -Math.abs(displayAmount);
                  description = t('points.txCommission');
                } else {
                  // For other transaction types, use the amount as-is
                  isPositive = displayAmount > 0;
                }

                return (
                  <TransactionItem key={index}>
                    <TransactionInfo>
                      <TransactionType>{description}</TransactionType>
                      <TransactionDate>
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </TransactionDate>
                    </TransactionInfo>
                    <TransactionAmount positive={isPositive}>
                      {isPositive ? '+' : ''}{displayAmount} {t('profile.points')}
                    </TransactionAmount>
                  </TransactionItem>
                );
              })
          ) : (
            <EmptyState>
              <h3>{t('points.noTransactions')}</h3>
            </EmptyState>
          )}
        </TransactionList>
      </HistoryCard>
    </PointsContainer>
  );
};

export default PointsPage;
